import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { AUTH } from '@/constants/testIds';
import { toast } from 'sonner';
import { BrandLockup } from '@/components/brand/BrandMark';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(u.role === 'cleaner' ? '/my-day' : '/');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const demo = async (role) => {
    setLoading(true);
    try {
      const creds = {
        admin: { email: 'admin@flexiclean.app', password: 'admin123' },
        cleaner: { email: 'cleaner@flexiclean.app', password: 'cleaner123' },
        customer: { email: 'customer@flexiclean.app', password: 'customer123' },
      }[role];
      const u = await login(creds.email, creds.password);
      toast.success(`Signed in as ${u.name}`);
      const dest = u.role === 'cleaner' ? '/my-day' : u.role === 'customer' ? '/portal' : '/';
      navigate(dest);
    } catch (e) {
      toast.error('Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-gradient-to-br from-[hsl(var(--background))] via-[hsl(var(--accent))] to-[hsl(var(--primary-subtle))]">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <BrandLockup className="gap-3" markClassName="h-10 w-10" />
          <CardTitle className="text-xl">Sign in to your workspace</CardTitle>
          <CardDescription>Cleaning & turnover operations, simplified.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid={AUTH.loginEmail} placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={e => setPassword(e.target.value)} data-testid={AUTH.loginPassword} placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-10" data-testid={AUTH.loginSubmit}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>

          {process.env.REACT_APP_DEMO_ENABLED !== 'false' && (
            <>
              <div className="my-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <div className="text-xs text-muted-foreground">or try a demo account</div>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" onClick={() => demo('admin')} data-testid={AUTH.demoLoginAdmin} disabled={loading}>
                  Demo Admin
                </Button>
                <Button type="button" variant="outline" onClick={() => demo('cleaner')} data-testid={AUTH.demoLoginCleaner} disabled={loading}>
                  Demo Cleaner
                </Button>
              </div>
              <Button type="button" variant="outline" onClick={() => demo('customer')} disabled={loading} className="mt-2 w-full">
                Demo Customer (portal)
              </Button>
            </>
          )}

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Are you a customer? <Link to="/portal/login" className="font-medium text-primary hover:underline">Customer portal</Link>
          </div>
          <div className="mt-2 text-center text-xs text-muted-foreground">
            Staff and customer accounts are created from inside FlexiClean — ask your admin for an invite.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
